/**
 * Paints the WebSocket token stream of the active chat task into the thread,
 * but only while the conversation it belongs to is on screen (CHAT-4).
 */
import { useEffect, type Dispatch, type SetStateAction } from 'react';

import { useTaskEvents } from '../../hooks/useTaskProgress';
import type { ChatMessage } from '../../types';
import { applyStreamDelta, hasRenderableStreamText, removeStreamBubble } from './streamingBubble';

export function useChatStreaming({
  activeTaskId,
  isStreamOnScreen,
  setMessages,
}: {
  activeTaskId: string | null;
  /** True while the task's conversation is the one shown. Read on every delta. */
  isStreamOnScreen: () => boolean;
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
}) {
  const { onStreamDelta, onStreamError } = useTaskEvents();

  useEffect(() => {
    if (!activeTaskId) return;

    const unregDelta = onStreamDelta(activeTaskId, (accumulatedText: string) => {
      if (!hasRenderableStreamText(accumulatedText) || !isStreamOnScreen()) return;
      setMessages((prev) => applyStreamDelta(prev, accumulatedText));
    });

    const unregError = onStreamError(activeTaskId, () => {
      if (!isStreamOnScreen()) return;
      setMessages(removeStreamBubble);
    });

    return () => { unregDelta(); unregError(); };
    // isStreamOnScreen reads refs; re-subscribing on its identity isn't needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTaskId, onStreamDelta, onStreamError, setMessages]);
}
